import type { Mutation, MutationOp } from '../mutations';
import type { BuildCtx } from '../spending/types';

/** RESERVED_KEYS (conventions §4): the appliers set them; a builder that puts them in values is a bug. */
const APPLIER_KEYS = ['id', 'userId', 'createdAt', 'updatedAt', 'deletedAt', 'clock'] as const;

export type ShoppingResult<T, E extends string> = ({ ok: true } & T) | { ok: false; error: E };

/** Same shape as Spending's and Budget's builder helpers, with source 'shopping'. */
export function shoppingMutation(
  ctx: BuildCtx,
  table: string,
  op: MutationOp,
  rowId: string,
  values?: Record<string, unknown>,
  opts: { ifAbsent?: true } = {},
): Mutation {
  for (const k of APPLIER_KEYS) {
    if (values && k in values) throw new Error(`Mutation values must not include '${k}'`);
  }
  return {
    id: ctx.newId(),
    table,
    op,
    rowId,
    ...(values ? { values } : {}),
    at: ctx.now,
    source: 'shopping',
    ...(opts.ifAbsent ? { ifAbsent: true as const } : {}),
  };
}

/** An item on the open list (shopping_items). Bought items keep their row with tripId set. */
export interface ShoppingItemLike {
  id: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  /** Centavos for the whole line (quantity included), or null. */
  estimateMinor: number | null;
  store: string | null;
  position: number;
  /** Set when the item was bought on a trip; the open list shows only tripId === null. */
  tripId: string | null;
  deletedAt: string | null;
}

/** One past purchase (shopping_trips). */
export interface ShoppingTripLike {
  id: string;
  date: string;
  store: string | null;
  totalMinor: number;
  /** The Spending expense it logged, or null when Spending was off. */
  expenseId: string | null;
  deletedAt: string | null;
}

/** One line of a trip (shopping_trip_items). A line with a price is a price point. */
export interface ShoppingTripItemLike {
  id: string;
  tripId: string;
  itemId: string | null;
  name: string;
  normalizedName: string;
  quantity: number | null;
  unit: string | null;
  /** What this line cost in centavos, or null when only the trip total is known. */
  priceMinor: number | null;
  position: number;
}

/** A trip line with a price, joined with its trip's date and store (spec §6 "price point"). */
export interface PricePoint {
  tripId: string;
  tripItemId: string;
  name: string;
  normalizedName: string;
  quantity: number | null;
  unit: string | null;
  priceMinor: number;
  store: string | null;
  date: string;
  /** Tie-break for two points on the same day (createdAt of the trip line). */
  createdAt: string;
}
